import React, { useEffect, useMemo, useState } from 'react';
import { Save, RotateCcw } from 'lucide-react';

import Button from '@components/common/Button';
import { Input } from '@components/common';
import SettingsSectionHeader from '@components/features/data-management-settings/SettingsSectionHeader';
import { formatListKey } from '@components/features/data-management-settings/settingsNav';
import { useReportDirty } from '@components/features/data-management-settings/useReportDirty';
import { useQueryClient } from '@tanstack/react-query';
import type { ReferenceList } from '@app-types/reference-list';
import { referenceListService } from '@services';
import { getApiErrorMessage } from '@utils/apiError';
import { REFERENCE_CATALOG_QUERY_KEY } from '@utils/referenceCatalog';
import { showToast } from '@utils/toast';
import styles from './ReferenceListsSettings.module.css';

interface DraftValue {
  code: string;
  label: string;
}

export interface ReferenceListEditorProps {
  list: ReferenceList;
  onDirtyChange?: (dirty: boolean) => void;
  onSaved?: (list: ReferenceList) => void;
}

function compareByCode(left: string, right: string): number {
  return left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' });
}

function valuesToDraft(list: ReferenceList): DraftValue[] {
  return [...list.values]
    .sort((left, right) => compareByCode(left.code, right.code))
    .map((value) => ({ code: value.code, label: value.label }));
}

function isDirty(draft: DraftValue[], list: ReferenceList): boolean {
  const source = valuesToDraft(list);
  if (draft.length !== source.length) return true;
  return draft.some((item, index) => item.code !== source[index].code || item.label !== source[index].label);
}

const LABEL_MAX_LENGTH = 255;

function validateDraftLabels(draft: DraftValue[]): string | null {
  const trimmed = draft.map((value) => value.label.trim());
  if (trimmed.some((label) => label === '')) {
    return 'All values must have a label';
  }
  if (trimmed.some((label) => label.includes('<') || label.includes('>'))) {
    return 'Labels cannot contain HTML';
  }
  if (trimmed.some((label) => label.length > LABEL_MAX_LENGTH)) {
    return `Labels must be no longer than ${LABEL_MAX_LENGTH} characters`;
  }
  const seen = new Set<string>();
  for (const label of trimmed) {
    const key = label.toLowerCase();
    if (seen.has(key)) {
      return 'Labels must be unique within this list';
    }
    seen.add(key);
  }
  return null;
}

const ReferenceListEditor: React.FC<ReferenceListEditorProps> = ({ list, onDirtyChange, onSaved }) => {
  const queryClient = useQueryClient();
  const [source, setSource] = useState(list);
  const [draft, setDraft] = useState<DraftValue[]>(() => valuesToDraft(list));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSource(list);
    setDraft(valuesToDraft(list));
  }, [list]);

  const dirty = useMemo(() => isDirty(draft, source), [draft, source]);
  useReportDirty(dirty, onDirtyChange);

  const handleLabelChange = (index: number, value: string) => {
    setDraft((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], label: value };
      return next;
    });
  };

  const handleReset = () => {
    setDraft(valuesToDraft(source));
  };

  const handleSave = async () => {
    const validationError = validateDraftLabels(draft);
    if (validationError) {
      showToast.error(validationError);
      return;
    }

    try {
      setSaving(true);
      const updated = await referenceListService.updateLabels(list.key, {
        values: draft.map((value) => ({ code: value.code, label: value.label.trim() })),
      });
      setSource(updated);
      setDraft(valuesToDraft(updated));
      await queryClient.invalidateQueries({ queryKey: REFERENCE_CATALOG_QUERY_KEY });
      onSaved?.(updated);
      showToast.success(`"${formatListKey(list.key)}" labels saved`);
    } catch (error) {
      showToast.error(getApiErrorMessage(error, 'Failed to save reference list'));
    } finally {
      setSaving(false);
    }
  };

  const title = formatListKey(list.key);
  const description = list.description || 'Edit display labels. Codes stay unchanged and match values stored in the system.';

  return (
    <div className={styles.container} data-test-id={`reference-list-${list.key}`}>
      <SettingsSectionHeader
        sticky
        title={title}
        description={description}
        actions={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleReset}
              disabled={!dirty || saving}
              dataTestId={`reference-list-reset-${list.key}`}
            >
              <RotateCcw size={14} />
              Reset
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void handleSave()}
              disabled={!dirty || saving}
              loading={saving}
              loadingText="Saving..."
              dataTestId={`reference-list-save-${list.key}`}
            >
              <Save size={14} />
              Save
            </Button>
          </>
        }
      />

      {draft.length === 0 ? (
        <div className={styles.empty}>This list has no values.</div>
      ) : (
        <div className={styles.valueList}>
          <div className={styles.valueHeader}>
            <span className={styles.valueIndex} />
            <span>Code</span>
            <span>Label</span>
          </div>
          {draft.map((value, index) => (
            <div key={value.code} className={styles.valueRow}>
              <span className={styles.valueIndex}>{index + 1}</span>
              <span className={styles.codeLabel}>{value.code}</span>
              <Input
                placeholder="Label"
                value={value.label}
                maxLength={255}
                onChange={(event) => handleLabelChange(index, event.target.value)}
                dataTestId={`reference-list-label-${list.key}-${value.code}`}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReferenceListEditor;
